import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  useGet360ReviewsByUserByUserIdQuery,
  FeedbackSurveyWithRelations,
} from "../../redux/vmsApi";
import {useRouter} from "../../hooks/useRouter";
import {generateQuartersForYear} from "../../utils/periodUtil";

interface OwnProps {
  subjectUserId: string;
  admin?: boolean;
}

// Generate all quarters for admin filtering (2025 and 2026)
const quarterOptions = [...generateQuartersForYear(2025), ...generateQuartersForYear(2026)];

// Mapping of question IDs to full questions
const questionMapping: { [key: string]: string } = {
  respectValues: 'Respect the WestProp Values evident in conduct',
  decisionValues: 'Makes decisions in line with WestProp values',
  encourageValues: 'Encourages team members to live the WestProp values',
  improveValues: 'What suggestions do you have to help improve living the WestProp values',
  goodDecisions: 'Makes good decisions and trusts the team when delegating tasks',
  highStandards: 'The team is working to a high standard and cohesively towards the high-level goals and objectives of the department',
  motivates: 'Motivates others to reach their goals',
  improveLeadership: 'What suggestions do you have to help improve leadership skills',
  controlEmotions: 'Controls emotions even in high pressure situations',
  awareness: 'Demonstrates an awareness of how actions and decisions affect others',
  strongSkills: 'Has strong skills and is able to interact well with others',
  empathy: 'Demonstrates empathy and is able to recognize and respond appropriately to the emotional state of others',
  selfMotivated: 'Is self motivated, striving to achieve goals and always looking for ways to do better',
  improveEmotional: 'What suggestions do you have to help improve emotional intelligence',
  urgentWork: 'Delivers urgent work without compromising on the details',
  meetsDeadlines: 'Meets deadlines and makes the best time use',
  exceedsExpectations: 'Exceeds expectations by delivering more than assigned work despite the tight schedule',
  improveProductivity: 'What suggestions do you have to help improve productivity',
  fullUnderstanding: 'Has full understanding of role and responsibility in the department',
  expertSkills: 'Is an expert and performs job responsibilities skillfully',
  improveJobKnowledge: 'What suggestions do you have to help improve job knowledge',
  gracefulDisagree: 'Even when disagreeing with others, does it gracefully and respectfully',
  adaptsEasily: 'Adapts easily to various situations and different people',
  equalTreatment: 'Equal in treatment of everyone and communicates with peers respectfully',
  improveInterpersonal: 'What suggestions do you have to help improve interpersonal skills',
  clearGoalsSelf: 'Sets clear goals, objectives and targets for self',
  clearGoalsTeam: 'Sets and communicates clear goals, objectives and targets for the team',
  performanceReviews: 'Conducts performance review meetings/appraisals with the team timorously and objectively',
  manageUnderperformance: 'Is proactive in managing underperformance of the team by initiating and monitoring appropriate interventions e.g. training',
  improvePerformance: 'What suggestions do you have to help improve performance management and execution',
  strength: 'What is the greatest strength and what should continue to do to grow and develop?',
  opportunity: 'What is the greatest opportunity and what can be done to improve performance?',
};

const sections = [
  { title: 'VALUES', questions: ['respectValues', 'decisionValues', 'encourageValues', 'improveValues'] },
  { title: 'LEADERSHIP SKILLS', questions: ['goodDecisions', 'highStandards', 'motivates', 'improveLeadership'] },
  { title: 'EMOTIONAL INTELLIGENCE', questions: ['controlEmotions', 'awareness', 'strongSkills', 'empathy', 'selfMotivated', 'improveEmotional'] },
  { title: 'PRODUCTIVITY', questions: ['urgentWork', 'meetsDeadlines', 'exceedsExpectations', 'improveProductivity'] },
  { title: 'JOB KNOWLEDGE', questions: ['fullUnderstanding', 'expertSkills', 'improveJobKnowledge'] },
  { title: 'INTERPERSONAL SKILLS', questions: ['gracefulDisagree', 'adaptsEasily', 'equalTreatment', 'improveInterpersonal'] },
  { title: 'PERFORMANCE MANAGEMENT', questions: ['clearGoalsSelf', 'clearGoalsTeam', 'performanceReviews', 'manageUnderperformance', 'improvePerformance'] },
];

// Rating labels for display
const ratingLabels: { [key: number]: { label: string; color: string } } = {
  1: { label: 'Very Poor', color: '#d32f2f' },
  2: { label: 'Poor', color: '#f44336' },
  3: { label: 'Below Average', color: '#ff5722' },
  4: { label: 'Slightly Below Average', color: '#ff9800' },
  5: { label: 'Average', color: '#ffc107' },
  6: { label: 'Slightly Above Average', color: '#cddc39' },
  7: { label: 'Good', color: '#8bc34a' },
  8: { label: 'Very Good', color: '#4caf50' },
  9: { label: 'Excellent', color: '#2e7d32' },
  10: { label: 'Outstanding', color: '#1b5e20' },
};

// Get color based on average rating (1-10 scale)
const getColorForRating = (rating: number): string => {
  if (rating >= 9) return '#1b5e20';
  if (rating >= 8) return '#2e7d32';
  if (rating >= 7) return '#4caf50';
  if (rating >= 6) return '#8bc34a';
  if (rating >= 5) return '#cddc39';
  if (rating >= 4) return '#ffc107';
  if (rating >= 3) return '#ff9800';
  if (rating >= 2) return '#ff5722';
  return '#f44336';
};

// Get label based on average rating (1-10 scale)
const getLabelForRating = (rating: number): string => {
  if (rating >= 9.5) return 'Outstanding';
  if (rating >= 8.5) return 'Excellent';
  if (rating >= 7.5) return 'Very Good';
  if (rating >= 6.5) return 'Good';
  if (rating >= 5.5) return 'Slightly Above Avg';
  if (rating >= 4.5) return 'Average';
  if (rating >= 3.5) return 'Slightly Below Avg';
  if (rating >= 2.5) return 'Below Average';
  if (rating >= 1.5) return 'Poor';
  return 'Very Poor';
};

const FeedbackSurveyResponses: React.FC<OwnProps> = ({subjectUserId, admin=false}) => {
  const router = useRouter();
  const [selectedQuarter, setSelectedQuarter] = useState<string>('');
  const [reviews, setReviews] = useState<FeedbackSurveyWithRelations[]>([]);
  const { data: userReviews } = useGet360ReviewsByUserByUserIdQuery({ userId: subjectUserId }, { skip: !subjectUserId });

  useEffect(() => {
    if (userReviews) {
      setReviews(userReviews.filter(review => review.reviewPeriod === selectedQuarter));
    }
  }, [userReviews, selectedQuarter]);

  const handleQuarterChange = (event: SelectChangeEvent) => {
    setSelectedQuarter(event.target.value);
  };

  // Process data for rating distribution bar chart
  const processDataForBarChart = (section: string) => {
    const data = sections.find(s => s.title === section)?.questions.filter(q => !q.includes('improve')).map(questionId => {
      const responses = reviews.flatMap(review => review.responses.filter(response => response.questionId === questionId));
      
      // Check for new rating system first, fallback to boolean for backwards compatibility
      const hasRatings = responses.some(r => r.responseRating !== undefined);
      
      if (hasRatings) {
        // New rating system - show distribution (1-10)
        const distribution = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0]; // ratings 1-10
        responses.forEach(r => {
          if (r.responseRating && r.responseRating >= 1 && r.responseRating <= 10) {
            distribution[r.responseRating - 1]++;
          }
        });
        return {
          question: questionMapping[questionId],
          '1-2': distribution[0] + distribution[1],
          '3-4': distribution[2] + distribution[3],
          '5-6': distribution[4] + distribution[5],
          '7-8': distribution[6] + distribution[7],
          '9-10': distribution[8] + distribution[9],
        };
      } else {
        // Backwards compatibility with boolean responses
        const trueCount = responses.filter(r => r.responseBoolean === true).length;
        const falseCount = responses.filter(r => r.responseBoolean === false).length;
        return {
          question: questionMapping[questionId],
          '1-2': falseCount,
          '3-4': 0,
          '5-6': 0,
          '7-8': 0,
          '9-10': trueCount,
        };
      }
    });
    return data || [];
  };

  // Calculate average rating for a section
  const calculateSectionAverage = (section: string) => {
    const sectionData = sections.find(s => s.title === section);
    if (!sectionData) return 0;

    const ratingQuestions = sectionData.questions.filter(q => !q.includes('improve'));
    let totalRating = 0;
    let totalResponses = 0;

    ratingQuestions.forEach(questionId => {
      const responses = reviews.flatMap(review => 
        review.responses.filter(response => response.questionId === questionId)
      );
      
      responses.forEach(r => {
        if (r.responseRating !== undefined) {
          totalRating += r.responseRating;
          totalResponses++;
        } else if (r.responseBoolean !== undefined) {
          // Backwards compatibility: true = 10, false = 1
          totalRating += r.responseBoolean ? 10 : 1;
          totalResponses++;
        }
      });
    });

    return totalResponses > 0 ? totalRating / totalResponses : 0;
  };

  // Calculate question-level stats
  const getQuestionStats = (questionId: string) => {
    const responses = reviews.flatMap(review => 
      review.responses.filter(response => response.questionId === questionId)
    );

    const ratings: number[] = [];
    responses.forEach(r => {
      if (r.responseRating !== undefined) {
        ratings.push(r.responseRating);
      } else if (r.responseBoolean !== undefined) {
        ratings.push(r.responseBoolean ? 10 : 1);
      }
    });

    if (ratings.length === 0) return { avg: 0, count: 0, min: 0, max: 0 };

    const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
    const min = Math.min(...ratings);
    const max = Math.max(...ratings);

    return { avg, count: ratings.length, min, max };
  };

  const chartSettings = {
    width: 700,
    height: 350,
    layout: 'horizontal' as const,
  };

  // Calculate overall average
  const overallAverage = sections.reduce((sum, section) => sum + calculateSectionAverage(section.title), 0) / sections.length;

  return (
    <Stack spacing={4} width="100%" p={10} pb={5}>
        {!admin &&
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Button color="primary" onClick={() => router.push('/reviews')}>
                <ArrowBackIcon/>
                <Typography color="primary" align="left">Dashboard</Typography>
              </Button>
            </Grid>
          </Grid>
        }
        <Typography variant='h1' gutterBottom style={{ fontWeight: 'bold', color: '#123456' }}>
          360° Feedback Overview
        </Typography>
        <FormControl variant="outlined" fullWidth required>
          <InputLabel>Review Period</InputLabel>
          <Select
            label="Review Period"
            value={selectedQuarter}
            onChange={handleQuarterChange}
          >
            {quarterOptions.map((quarter, index) => (
              <MenuItem key={index} value={quarter}>{quarter}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {reviews.length > 0 && (
          <>
            {/* Overall Summary */}
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#123456', fontWeight: 'bold' }}>
                Overall Summary for {selectedQuarter}
              </Typography>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h2" sx={{ color: getColorForRating(overallAverage), fontWeight: 'bold' }}>
                      {((overallAverage / 10) * 100).toFixed(0)}%
                    </Typography>
                    <Chip 
                      label={getLabelForRating(overallAverage)}
                      sx={{ backgroundColor: getColorForRating(overallAverage), color: 'white', mt: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Based on {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="subtitle2" gutterBottom>Category Averages</Typography>
                  {sections.map(section => {
                    const avg = calculateSectionAverage(section.title);
                    const avgPercent = (avg / 10) * 100;
                    return (
                      <Box key={section.title} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{section.title}</Typography>
                          <Typography variant="body2" sx={{ color: getColorForRating(avg), fontWeight: 'bold' }}>
                            {avgPercent.toFixed(0)}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={avgPercent} 
                          sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getColorForRating(avg),
                            }
                          }}
                        />
                      </Box>
                    );
                  })}
                </Grid>
              </Grid>
            </Paper>

            {/* Detailed Section Analysis */}
            {sections.map((section, sectionIndex) => {
              const sectionAvg = calculateSectionAverage(section.title);
              const chartData = processDataForBarChart(section.title);
              const ratingQuestions = section.questions.filter(q => !q.includes('improve'));

              return (
                <Paper elevation={3} sx={{ p: 3, mb: 3 }} key={sectionIndex}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" sx={{ color: '#123456', fontWeight: 'bold' }}>
                      {section.title}
                    </Typography>
                    <Chip 
                      label={`Avg: ${sectionAvg.toFixed(1)} - ${getLabelForRating(sectionAvg)}`}
                      sx={{ backgroundColor: getColorForRating(sectionAvg), color: 'white' }}
                    />
                  </Box>
                  
                  <Grid container spacing={3}>
                    {/* Bar Chart */}
                    <Grid item xs={12} md={7}>
                      <BarChart
                        series={[
                          { dataKey: '1-2', label: '1-2 (Poor)', stack: 'A', color: '#f44336' },
                          { dataKey: '3-4', label: '3-4 (Below Avg)', stack: 'A', color: '#ff9800' },
                          { dataKey: '5-6', label: '5-6 (Average)', stack: 'A', color: '#ffc107' },
                          { dataKey: '7-8', label: '7-8 (Good)', stack: 'A', color: '#8bc34a' },
                          { dataKey: '9-10', label: '9-10 (Excellent)', stack: 'A', color: '#4caf50' },
                        ]}
                        yAxis={[{ scaleType: 'band', dataKey: 'question' }]}
                        dataset={chartData}
                        {...chartSettings}
                      />
                    </Grid>

                    {/* Question Stats Table */}
                    <Grid item xs={12} md={5}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Question</TableCell>
                              <TableCell align="center">Avg</TableCell>
                              <TableCell align="center">Responses</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ratingQuestions.map(questionId => {
                              const stats = getQuestionStats(questionId);
                              return (
                                <TableRow key={questionId}>
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    {questionMapping[questionId].substring(0, 50)}...
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip 
                                      label={stats.avg.toFixed(1)}
                                      size="small"
                                      sx={{ 
                                        backgroundColor: getColorForRating(stats.avg), 
                                        color: 'white',
                                        minWidth: 40
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">{stats.count}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>

                  {/* Text Responses */}
                  {section.questions.filter(q => q.includes('improve')).map(questionId => {
                    const textResponses = reviews.flatMap(review => 
                      review.responses.filter(response => response.questionId === questionId && response.responseText)
                    );
                    if (textResponses.length > 0) {
                      return (
                        <Box key={questionId} sx={{ mt: 3 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {questionMapping[questionId]}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {textResponses.length} response{textResponses.length !== 1 ? 's' : ''}
                          </Typography>
                          {textResponses.map((response, responseIndex) => (
                            <Paper key={responseIndex} sx={{ p: 2, mb: 1, backgroundColor: '#f9f9f9' }}>
                              <Typography variant="body2">{response.responseText}</Typography>
                            </Paper>
                          ))}
                        </Box>
                      );
                    }
                    return null;
                  })}
                </Paper>
              );
            })}

            {/* Strengths and Opportunities */}
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h5" sx={{ color: '#123456', fontWeight: 'bold', mb: 2 }}>
                Strengths & Opportunities
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" color="success.main" gutterBottom>Greatest Strengths</Typography>
                  {reviews.flatMap(review => 
                    review.responses.filter(r => r.questionId === 'strength' && r.responseText)
                  ).map((response, idx) => (
                    <Paper key={idx} sx={{ p: 2, mb: 1, backgroundColor: '#e8f5e9' }}>
                      <Typography variant="body2">{response.responseText}</Typography>
                    </Paper>
                  ))}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" color="info.main" gutterBottom>Opportunities for Growth</Typography>
                  {reviews.flatMap(review => 
                    review.responses.filter(r => r.questionId === 'opportunity' && r.responseText)
                  ).map((response, idx) => (
                    <Paper key={idx} sx={{ p: 2, mb: 1, backgroundColor: '#e3f2fd' }}>
                      <Typography variant="body2">{response.responseText}</Typography>
                    </Paper>
                  ))}
                </Grid>
              </Grid>
            </Paper>
          </>
        )}

        {reviews.length === 0 && selectedQuarter && (
          <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No feedback responses found for {selectedQuarter}
            </Typography>
          </Paper>
        )}
      </Stack>
  );
};

export default FeedbackSurveyResponses;
